"use client";
import { useCallback, useEffect, useState } from "react";

import {
  loyaltyApi,
  type LoyaltyBalanceResponse,
  type LoyaltyRewardResponse,
  type LoyaltyTransactionResponse,
} from "@/api/services";
import { useAuth } from "@/store/auth";
import { ApiError } from "@/api/http";

export type LoyaltyBalance = {
  points: number;
  programType?: string | null;
  cashback: number;
  pointsExpiringSoon: number;
  tier?: string | null;
  nextTier?: { name: string; pointsAway: number } | null;
  lifetimeSpend: number;
  level?: string | null;
  currency?: string | null;
  expiringInDays: number;
};

export type LoyaltyReward = {
  id: string;
  name: string;
  pointsCost: number;
  description?: string;
};

export type LoyaltyTransaction = {
  id: string;
  type: string;
  description: string;
  status?: string | null;
  pointsChange: number;
  cashbackChange: number;
  amount: number;
  orderId?: string | null;
  orderNumber?: string | null;
  createdAt?: string | null;
};

export type UseLoyaltyResult = {
  loading: boolean;
  error: Error | null;
  balance: LoyaltyBalance | null;
  rewards: LoyaltyReward[];
  refresh: () => Promise<void>;
  isNotFound: boolean;
};

export type UseLoyaltyTransactionsResult = {
  loading: boolean;
  error: Error | null;
  transactions: LoyaltyTransaction[];
  refresh: () => Promise<void>;
};

function firstNumber(...values: unknown[]): number {
  for (const v of values) {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim()) {
      const parsed = Number(v);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
}

function adaptBalance(
  raw: LoyaltyBalanceResponse | null,
): LoyaltyBalance | null {
  if (!raw) return null;
  const points = firstNumber(
    raw.points,
    raw.pointsBalance,
    raw.availablePoints,
    raw.totalPoints,
    raw.currentPoints,
  );
  const cashback = firstNumber(
    raw.cashback,
    raw.cashbackBalance,
    raw.availableCashback,
    raw.currentCashback,
  );
  const pointsExpiringSoon = firstNumber(raw.pointsExpiringSoon);
  const tier = raw.tierName ?? raw.tier ?? null;
  let nextTier: LoyaltyBalance["nextTier"] = null;
  if (raw.nextTier && raw.nextTier.name) {
    nextTier = {
      name: String(raw.nextTier.name),
      pointsAway:
        typeof raw.nextTier.pointsAway === "number"
          ? raw.nextTier.pointsAway
          : 0,
    };
  }
  return {
    points,
    programType: raw.programType ?? null,
    cashback,
    pointsExpiringSoon,
    tier,
    nextTier,
    lifetimeSpend: firstNumber(raw.lifetimeSpend),
    level: raw.level ? String(raw.level) : null,
    currency: raw.currency ? String(raw.currency) : null,
    expiringInDays: firstNumber(raw.expiringInDays),
  };
}

function adaptRewards(
  raw: LoyaltyRewardResponse[] | null | undefined,
): LoyaltyReward[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .filter((r): r is LoyaltyRewardResponse => !!r && typeof r.id === "string")
    .map((r) => ({
      id: r.id,
      name: r.name ?? "Reward",
      pointsCost: typeof r.pointsCost === "number" ? r.pointsCost : 0,
      description: r.description ?? undefined,
    }));
}

function adaptTransactions(
  raw: LoyaltyTransactionResponse[] | null | undefined,
): LoyaltyTransaction[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((tx, idx) => {
    const type =
      tx.type ??
      tx.transactionType ??
      tx.eventType ??
      (firstNumber(tx.pointsChange, tx.points) < 0 ? "redemption" : "accrual");
    const createdAt = tx.createdAt ?? tx.timestamp ?? tx.date ?? null;
    return {
      id: String(tx.id ?? `${createdAt ?? "tx"}-${idx}`),
      type: String(type),
      description:
        tx.description ??
        tx.reason ??
        tx.orderNumber ??
        String(type)
          .replace(/[_-]+/g, " ")
          .replace(/\b\w/g, (m) => m.toUpperCase()),
      status: tx.status ?? null,
      pointsChange: firstNumber(tx.pointsChange, tx.points),
      cashbackChange: firstNumber(tx.cashbackChange, tx.cashback),
      amount: firstNumber(tx.amount),
      orderId: tx.orderId ?? null,
      orderNumber: tx.orderNumber ?? null,
      createdAt,
    };
  });
}

/**
 * Fetches the current customer's loyalty balance and rewards.
 *
 * The endpoints are loyalty-program dependent (see `loyaltyApi.getRewards`
 * for the 404 fallback). When the customer is not signed in this hook
 * stays in its idle state — `balance: null`, `rewards: []`, `loading: false`.
 */
export function useLoyalty(): UseLoyaltyResult {
  const { isLoggedIn, user } = useAuth();
  const [balance, setBalance] = useState<LoyaltyBalance | null>(null);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setBalance(null);
      setRewards([]);
      setError(null);
      setLoading(false);
      setIsNotFound(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const balanceRaw = await loyaltyApi.getBalance(user.id);
      const adaptedBalance = adaptBalance(balanceRaw);
      // Removed loyaltyApi.getRewards call per user request to hide the API call.
      const adaptedRewards = adaptRewards(balanceRaw?.rewards ?? null);
      setBalance(adaptedBalance);
      setRewards(adaptedRewards);
      setIsNotFound(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setIsNotFound(true);
        setBalance(null);
        setRewards([]);
        setError(null);
      } else {
        console.warn("[useLoyalty] fetch failed", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setBalance(null);
        setRewards([]);
        setIsNotFound(false);
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { loading, error, balance, rewards, refresh, isNotFound };
}

/** Fetches loyalty point/cashback transaction history for the signed-in user. */
export function useLoyaltyTransactions(): UseLoyaltyTransactionsResult {
  const { isLoggedIn, user } = useAuth();
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !user?.id) {
      setTransactions([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await loyaltyApi.getTransactions(user.id);
      setTransactions(adaptTransactions(raw));
    } catch (err) {
      console.warn("[useLoyaltyTransactions] fetch failed", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, user?.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { loading, error, transactions, refresh };
}
